import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Grid, Button, IconButton, useMediaQuery, Menu, MenuItem, ListItemIcon, Stack } from '@mui/material';
import { Delete, MoreVert, Edit } from '@mui/icons-material';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { useLoadingContext } from '../../../context/loading/LoadingContext';
import { useToast, ToastType } from '../../../context/toast/ToastContext';
import { UOMSnapshotListener, deleteUnitOfMeasure } from '../../../repository/UOMDAO';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import { useUserContext } from '../../../context/user_context/UserContext';
import ConfirmDeleteModal from '../../../components/modals/ConfirmDeleteModal';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import EditUOMModal from './modals/EditUOMModal';
import { handleMenuClick, handleMenuClose } from '../../../components/utils/MenuHandlers';

const UnitOfMeasureDetail = () => {
  const { uomId } = useParams();
  const { organization } = useOrganizationContext();
  const { setLoadingData } = useLoadingContext();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { firebaseUser } = useUserContext();
  const [uom, setUOM] = useState();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [anchorEl, setAnchorEl] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (firebaseUser) {
      UOMSnapshotListener(firebaseUser.organization, uomId, setUOM);
    }
  }, [firebaseUser, uomId]);

  const handleDelete = async () => {
      setLoadingData(true);

      try {
          const result = await deleteUnitOfMeasure(organization.id, uomId);

          if (result.success) {
              showToast(t(StringKeys.SUCCESSFULLY_DELETED_X, {x: `${uom.uom}`}), ToastType.SUCCESS);
              navigate('/unit-of-measure', { replace: true });
          } else {
              showToast(t(StringKeys.FAILED_TO_DELETE_X, {x:`${uom.uom}`}), ToastType.DANGER);
              console.error("Error deleting unit of measure:", result.error);
          }
      } catch (error) {
          showToast(t(StringKeys.FAILED_TO_DELETE_X, {x:`${uom.uom}`}), ToastType.DANGER);
        } finally {
          setLoadingData(false);
      }
  };

  if (!uom) return null;

  return (
    <AppSidebar
      content={
        <>
          <Grid container alignItems="center" spacing={0}>
            {!isMobile && hasPermission(Modules.UNIT_OF_MEASURE, PermissionTypes.WRITE) && (
              <Grid item>
                <Stack direction="row" spacing={2}>
                  <Button 
                    variant="text" 
                    startIcon={<Edit />} 
                    onClick={() => setShowEditModal(true)}
                    sx={{ textTransform: 'none', fontSize: '1rem' }}
                  >
                    {t(StringKeys.EDIT)}
                  </Button>
                  <Button 
                    variant="text" 
                    startIcon={<Delete />} 
                    onClick={() => setConfirmOpen(true)} 
                    sx={{ textTransform: 'none', fontSize: '1rem' }}
                  >
                    {t(StringKeys.DELETE)}
                  </Button>
                </Stack>
              </Grid>
            )}
            {isMobile && hasPermission(Modules.UNIT_OF_MEASURE, PermissionTypes.WRITE) && (
              <Grid item>
                <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                  <MoreVert />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => handleMenuClose(setAnchorEl)}
                >
                  <MenuItem onClick={() => { setShowEditModal(true); handleMenuClose(setAnchorEl); }}>
                    <ListItemIcon>
                      <Edit fontSize="small" />
                    </ListItemIcon>
                    {t(StringKeys.EDIT)}
                  </MenuItem>
                  <MenuItem onClick={() => { setConfirmOpen(true); handleMenuClose(setAnchorEl); }}>
                    <ListItemIcon>
                      <Delete fontSize="small" />
                    </ListItemIcon>
                    {t(StringKeys.DELETE)}
                  </MenuItem>
                </Menu>
              </Grid>
            )}
          </Grid>
          <Card variant="outlined" sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant={isMobile ? "h6" : "h5"}>{uom.uom}</Typography>
              <Typography>{t(StringKeys.CAPACITY)}: {uom.capacity}</Typography>
            </CardContent>
          </Card>
          <EditUOMModal
            show={showEditModal}
            onHide={() => setShowEditModal(false)}
            uom={uom}
          />
          <ConfirmDeleteModal
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={() => { handleDelete(); setConfirmOpen(false); }}
            item={uom.uom}
          />
        </>
      }
    />
  );
};

export default UnitOfMeasureDetail;